import { useEffect, useState, lazy, Suspense } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Building, Home, ChevronRight, Loader2, Eye } from 'lucide-react';
import { LeadCaptureForm } from '@/components/forms/LeadCaptureForm';
import { Header } from '@/components/layout/Header';
import { TownNavBar } from '@/components/layout/TownNavBar';
import { AreaNavBar } from '@/components/layout/AreaNavBar';
import { Footer } from '@/components/layout/Footer';
import { Badge } from '@/components/ui/badge';
import { RealtorCard } from '@/components/realtor/RealtorCard';
import { StructuredData } from '@/components/seo/StructuredData';
import { RecentBlogPostsCard, type RecentBlogPost } from '@/components/blog/RecentBlogPostsCard';
import { ImageGalleryCarousel, GalleryImage } from '@/components/gallery/ImageGalleryCarousel';

import { TownStatistics } from '@/components/town/TownStatistics';
// Lazy-load TownMap to defer loading Leaflet (~40KB) until needed
const TownMap = lazy(() => import('@/components/town/TownMap').then(m => ({ default: m.TownMap })));
import { sanitizeHtml } from '@/lib/sanitize';
import { PublicThemeProvider } from '@/components/theme/ThemeProvider';
import { gaEvents } from '@/hooks/useGoogleAnalytics';
import { useLoanOfficerAdForTown } from '@/hooks/useLoanOfficerAd';

interface ContentSection {
  heading: string;
  content: string;
  image_url?: string; // Legacy field
  gallery_images?: GalleryImage[]; // New multi-image support
}

interface TownData {
  id: string;
  name: string;
  slug: string;
  state: string | null;
  hero_headline: string | null;
  hero_subheadline: string | null;
  hero_image_url: string | null;
  hero_image_attribution: string | null;
  gallery_images: GalleryImage[] | null;
  intro_summary: string | null;
  about_town: string | null;
  location_access: string | null;
  schools_amenities: string | null;
  location_sections: ContentSection[] | null;
  amenities_sections: ContentSection[] | null;
  highlights_json: any;
  faqs_json: any;
  zip_codes: string[] | null;
  site_id: string;
  median_home_price: number | null;
  market_trend_percent: number | null;
  avg_days_on_market: number | null;
  population: number | null;
}

interface AreaData {
  id: string;
  name: string;
  slug: string;
  intro_summary: string | null;
}

interface NeighboringTown {
  id: string;
  name: string;
  slug: string;
  state: string | null;
}

interface HeaderTown {
  id: string;
  name: string;
  slug: string;
}

interface ListingData {
  id: string;
  address: string;
  city: string;
  state: string;
  price: number | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  image_url: string | null;
}

interface SiteData {
  id: string;
  name: string;
  favicon_url: string | null;
  theme: string;
  header_font: string | null;
  enable_market_value_report?: boolean;
  enable_community_guide?: boolean;
  enable_mortgage_info?: boolean;
  bing_auth_code?: string | null;
  realtor_assigned?: boolean;
  seller_ad_image_url?: string | null;
  mortgage_ad_image_url?: string | null;
}

// Public-safe realtor data (from public_realtor_profiles view)
interface RealtorData {
  id: string;
  first_name: string | null;
  last_name: string | null;
  photo_url: string | null;
  logo_url: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  license_numbers: string | null;
  about_me: string | null;
  title_preference?: string | null;
  ad_image_url?: string | null;
}

export default function TownPage() {
  const { townSlug } = useParams<{ townSlug: string }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';
  
  const [town, setTown] = useState<TownData | null>(null);
  const [site, setSite] = useState<SiteData | null>(null);
  const [realtor, setRealtor] = useState<RealtorData | null>(null);
  const [areas, setAreas] = useState<AreaData[]>([]);
  const [neighboringTowns, setNeighboringTowns] = useState<NeighboringTown[]>([]);
  const [headerTowns, setHeaderTowns] = useState<HeaderTown[]>([]);
  const [listings, setListings] = useState<ListingData[]>([]);
  const [recentBlogPosts, setRecentBlogPosts] = useState<RecentBlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Loan officer ad - prioritizes loan officer ad over site mortgage ad
  const { data: loanOfficerAd } = useLoanOfficerAdForTown(town?.id || null);
  const mortgageAdImageUrl = loanOfficerAd?.ad_image_url || site?.mortgage_ad_image_url;
  const mortgageAdAlt = loanOfficerAd 
    ? `${loanOfficerAd.first_name} ${loanOfficerAd.last_name} - NMLS: ${loanOfficerAd.license_number || 'N/A'} - Mortgage Services`
    : 'Mortgage Information and Tips';

  useEffect(() => {
    if (townSlug) {
      fetchTownData();
    }
  }, [townSlug, isPreview]);

  const fetchTownData = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      // First, resolve the site by hostname for multi-tenancy
      const hostname = window.location.hostname;
      const isDevelopment = 
        hostname === 'localhost' || 
        hostname.includes('lovable.app') ||
        hostname.includes('127.0.0.1');

      let siteId: string | null = null;

      if (!isDevelopment) {
        // Production: Try to match by primary_domain
        const hostWithoutWww = hostname.replace(/^www\./, '');
        const hostWithWww = hostname.startsWith('www.') ? hostname : `www.${hostname}`;

        const { data: domainMatch } = await supabase
          .from('sites_public' as any)
          .select('id')
          .eq('status', 'active')
          .or(`primary_domain.eq.${hostname},primary_domain.eq.${hostWithoutWww},primary_domain.eq.${hostWithWww}`)
          .limit(1)
          .maybeSingle();

        if (domainMatch) {
          siteId = (domainMatch as any).id;
          console.log('Town page: Site resolved by domain:', hostname, '->', siteId);
        }
      }

      // Fallback to first active site for development (use oldest site)
      if (!siteId) {
        const { data: fallbackSite } = await supabase
          .from('sites_public' as any)
          .select('id')
          .eq('status', 'active')
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        
        siteId = (fallbackSite as any)?.id || null;
      }

      if (!siteId) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Fetch town by slug AND site_id for multi-tenancy
      let townQuery = supabase
        .from('towns')
        .select('*')
        .eq('slug', townSlug)
        .eq('site_id', siteId);
      
      if (!isPreview) {
        townQuery = townQuery.eq('is_published', true);
      }
      
      const { data: townData, error: townError } = await townQuery.single();

      if (townError || !townData) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      // Cast JSONB fields to proper types
      setTown({
        ...townData,
        location_sections: Array.isArray(townData.location_sections) ? (townData.location_sections as unknown as ContentSection[]) : null,
        amenities_sections: Array.isArray(townData.amenities_sections) ? (townData.amenities_sections as unknown as ContentSection[]) : null,
        gallery_images: Array.isArray(townData.gallery_images) ? (townData.gallery_images as unknown as GalleryImage[]) : null,
      } as TownData);

      // Fetch site, areas, listings, realtor, and neighboring towns in parallel
      let areasQuery = supabase.from('areas').select('id, name, slug, intro_summary').eq('town_id', townData.id).order('name');
      if (!isPreview) {
        areasQuery = areasQuery.eq('is_published', true);
      }

      // Neighboring towns query - other towns in the same site (excluding current)
      let neighboringQuery = supabase
        .from('towns')
        .select('id, name, slug, state')
        .eq('site_id', townData.site_id)
        .neq('id', townData.id)
        .order('name')
        .limit(6);
      if (!isPreview) {
        neighboringQuery = neighboringQuery.eq('is_published', true);
      }

      // Header towns query - all towns in the site
      let headerTownsQuery = supabase
        .from('towns')
        .select('id, name, slug')
        .eq('site_id', townData.site_id)
        .order('name');
      if (!isPreview) {
        headerTownsQuery = headerTownsQuery.eq('is_published', true);
      }
      
      const [siteRes, areasRes, listingsRes, realtorRes, neighboringRes, headerTownsRes, recentPostsRes] = await Promise.all([
        supabase.from('sites_public' as any).select('id, name, favicon_url, theme, header_font, enable_market_value_report, enable_community_guide, enable_mortgage_info, bing_auth_code, realtor_assigned, seller_ad_image_url, mortgage_ad_image_url').eq('id', townData.site_id).maybeSingle(),
        areasQuery,
        supabase.from('listings').select('id, address, city, state, price, beds, baths, sqft, image_url').eq('town_id', townData.id).eq('status', 'active').limit(6),
        supabase.from('realtor_profiles').select('id, first_name, last_name, photo_url, logo_url, phone, city, state, license_numbers, about_me, title_preference, ad_image_url').eq('site_id', townData.site_id).single(),
        neighboringQuery,
        headerTownsQuery,
        supabase
          .from('blog_posts')
          .select('id, title, slug, category, main_image_url')
          .eq('site_id', townData.site_id)
          .eq('is_published', true)
          .contains('target_town_ids', [townData.id])
          .order('published_at', { ascending: false })
          .limit(3),
      ]);

      if (siteRes.data) setSite(siteRes.data as unknown as SiteData);
      if (areasRes.data) setAreas(areasRes.data);
      if (listingsRes.data) setListings(listingsRes.data);
      if (realtorRes.data) setRealtor(realtorRes.data);
      if (neighboringRes.data) setNeighboringTowns(neighboringRes.data);
      if (headerTownsRes.data) setHeaderTowns(headerTownsRes.data);
      // Town-targeted posts preferred; fall back to recent site-wide posts if none target this town
      if (recentPostsRes.data && recentPostsRes.data.length > 0) {
        setRecentBlogPosts(recentPostsRes.data as RecentBlogPost[]);
      } else {
        const { data: fallbackPosts } = await supabase
          .from('blog_posts')
          .select('id, title, slug, category, main_image_url')
          .eq('site_id', townData.site_id)
          .eq('is_published', true)
          .order('published_at', { ascending: false })
          .limit(3);
        if (fallbackPosts) setRecentBlogPosts(fallbackPosts as RecentBlogPost[]);
      }

      // Track town view event
      gaEvents.viewTown(townData.id, townData.name);
    } catch (error) {
      console.error('Error fetching town:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading...</title>
          <meta name="description" content="" />
          <meta property="og:title" content="" />
          <meta property="og:description" content="" />
          <meta name="twitter:title" content="" />
          <meta name="twitter:description" content="" />
        </Helmet>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (notFound || !town) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header siteName="Real Estate" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-display font-bold mb-4">Town Not Found</h1>
            <p className="text-muted-foreground mb-6">The town you're looking for doesn't exist or isn't published yet.</p>
            <Button asChild>
              <Link to="/">Return Home</Link>
            </Button>
          </div>
        </main>
        <Footer siteName="Real Estate" />
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  return (
    <PublicThemeProvider themeId={site?.theme || 'classic'} headerFontId={site?.header_font || 'playfair-display'}>
      <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{town.name}, {town.state || 'MA'} Real Estate | {site?.name || 'Real Estate'}</title>
        <meta name="description" content={town.intro_summary || `Explore real estate in ${town.name}, ${town.state || 'MA'}`} />
        {site?.bing_auth_code && <meta name="msvalidate.01" content={site.bing_auth_code} />}
        {/* Canonical URL is injected by Cloudflare worker using site's canonical_base_url */}
        <meta name="robots" content="index, follow" />
        
        {/* Preload LCP hero image with high priority to reduce resource load delay */}
        {town.hero_image_url && <link rel="preload" as="image" href={town.hero_image_url} fetchPriority="high" />}
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={site?.name || 'Real Estate'} />
        <meta property="og:title" content={`${town.name}, ${town.state || 'MA'} Real Estate | ${site?.name || 'Real Estate'}`} />
        <meta property="og:description" content={town.intro_summary || `Explore real estate in ${town.name}, ${town.state || 'MA'}`} />
        {/* og:url and og:image are injected by Cloudflare worker */}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${town.name}, ${town.state || 'MA'} Real Estate | ${site?.name || 'Real Estate'}`} />
        <meta name="twitter:description" content={town.intro_summary || `Explore real estate in ${town.name}, ${town.state || 'MA'}`} />
      </Helmet>
      <StructuredData
        realtor={realtor}
        siteName={site?.name || 'Real Estate'}
        town={{ name: town.name, state: town.state, zip_codes: town.zip_codes }}
        pageUrl={typeof window !== 'undefined' ? window.location.href : ''}
      />
      <Header
        siteName={site?.name || 'Real Estate'} 
        logoUrl={realtor?.logo_url} 
        faviconUrl={site?.favicon_url}
        enableMarketValueReport={site?.enable_market_value_report !== false}
        enableCommunityGuide={site?.enable_community_guide !== false}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
      />
      
      {/* Town Navigation Bar */}
      <TownNavBar 
        towns={headerTowns} 
        currentTownSlug={town.slug} 
        isPreview={isPreview}
        enableMarketValueReport={site?.enable_market_value_report !== false}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
        realtorFirstName={realtor?.first_name}
        themeId={site?.theme}
      />
      
      {/* Area Navigation Bar */}
      <AreaNavBar areas={areas} townSlug={town.slug} isPreview={isPreview} />
      
      {/* Preview Mode Banner */}
      {isPreview && (
        <div className="bg-amber-500 text-amber-950 py-2 px-4 text-center text-sm font-medium flex items-center justify-center gap-2">
          <Eye className="h-4 w-4" />
          Preview Mode — This page is not published yet
        </div>
      )}
      
      <main className="flex-1">
        {/* Hero Section */}
        <section 
          className="relative bg-gradient-hero text-white py-6 md:py-10"
          style={town.hero_image_url ? {
            backgroundImage: `linear-gradient(to bottom, rgba(30, 41, 59, 0.85), rgba(30, 41, 59, 0.95)), url(${town.hero_image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          } : undefined}
        >
          <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
            <nav className="flex items-center gap-2 text-sm mb-6 opacity-80">
              <Link to="/" className="hover:underline">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to="/towns" className="hover:underline">Towns</Link>
              <ChevronRight className="h-4 w-4" />
              <span>{town.name}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-display font-bold mb-2">
              {town.name}, {town.state || 'MA'} Real Estate
            </h1>
            <h2 className="text-2xl md:text-3xl font-display font-medium mb-4 opacity-90">
              {town.hero_headline || `Real Estate Services in ${town.name}, ${town.state === 'MA' ? 'Massachusetts' : town.state || 'Massachusetts'}`}
            </h2>
            <p className="text-xl opacity-80">
              {town.hero_subheadline || town.intro_summary || `Discover homes and properties in ${town.name}.`}
            </p>
          </div>
        </section>

        {/* About Section */}
        {town.about_town && (
          <section className="py-6 md:py-8">
            <div className="container px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-10 gap-8 lg:gap-12">
                {/* Left: About content */}
                <div className="md:col-span-7">
                  {/* Gallery Carousel or Single Image */}
                  {(() => {
                    // Build gallery from gallery_images or fall back to hero_image_url
                    const gallery: GalleryImage[] = town.gallery_images && town.gallery_images.length > 0
                      ? town.gallery_images
                      : town.hero_image_url
                        ? [{ url: town.hero_image_url, attribution: town.hero_image_attribution || undefined }]
                        : [];
                    
                    if (gallery.length > 0) {
                      return (
                        <ImageGalleryCarousel 
                          images={gallery} 
                          altPrefix={`${town.name}, ${town.state || 'MA'}`}
                          isHero={true}
                        />
                      );
                    }
                    return null;
                  })()}
                  <h2 className="text-3xl font-display font-bold mb-6">About {town.name}</h2>
                  <div 
                    className="prose prose-lg max-w-none text-muted-foreground [&>p]:mb-4 [&>p:last-child]:mb-0"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(town.about_town) }}
                  />
                </div>
                {/* Right: Latest Articles + Realtor + Lead form */}
                <div className="md:col-span-3 space-y-6">
                  <RecentBlogPostsCard posts={recentBlogPosts} />
                  
                  {site?.realtor_assigned !== false && realtor && (
                    <RealtorCard realtor={realtor} />
                  )}
                  <Card className="p-6 lg:p-8">
                    <h3 className="text-2xl font-display font-bold mb-2 text-center">
                      Interested in {town.name}?
                    </h3>
                    <p className="text-muted-foreground text-center mb-6">
                      Get in touch and we'll help you find your perfect home.
                    </p>
                    <LeadCaptureForm 
                      siteId={town.site_id} 
                      townId={town.id}
                      townName={town.name}
                      siteDomain={window.location.hostname}
                      pagePath={`/${town.slug}`}
                    />
                  </Card>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Areas Section */}
        {areas.length > 0 && (
          <section className="py-12 md:py-16 bg-muted">
            <div className="container px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-display font-bold mb-8">
                Neighborhoods in {town.name}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {areas.map((area) => (
                  <Link key={area.id} to={`/${town.slug}/${area.slug}${isPreview ? '?preview=true' : ''}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3">
                          <Building className="h-5 w-5 text-primary mt-1 shrink-0" />
                          <div>
                            <h3 className="font-semibold text-lg mb-2">{area.name}</h3>
                            {area.intro_summary && (
                              <p className="text-sm text-muted-foreground line-clamp-2">
                                {area.intro_summary}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Town Statistics Section */}
        <TownStatistics
          townName={town.name}
          state={town.state}
          zipCodes={town.zip_codes}
          areasCount={areas.length}
          listingsCount={listings.length}
          medianHomePrice={town.median_home_price}
          marketTrendPercent={town.market_trend_percent}
          avgDaysOnMarket={town.avg_days_on_market}
          population={town.population}
        />

        {/* Town Map Section - Lazy loaded to defer Leaflet bundle */}
        <Suspense fallback={
          <section className="py-12 md:py-16 bg-muted">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="h-[500px] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            </div>
          </section>
        }>
          <TownMap
            townName={town.name}
            state={town.state}
            zipCodes={town.zip_codes}
          />
        </Suspense>

        {/* Location & Amenities - Structured Sections */}
        {(town.location_sections?.length || town.amenities_sections?.length || town.location_access || town.schools_amenities) && (
          <section className="py-12 md:py-16 bg-muted">
            <div className="container px-4 sm:px-6 lg:px-8">
              <div className="grid md:grid-cols-2 gap-12">
                {/* Location Column */}
                <div>
                  <h2 className="text-3xl font-display font-bold mb-8">Location & Access</h2>
                  {town.location_sections && town.location_sections.length > 0 ? (
                    <div className="space-y-8">
                      {town.location_sections.map((section, index) => {
                        // Support both new gallery_images and legacy image_url
                        const sectionGallery: GalleryImage[] = section.gallery_images?.length 
                          ? section.gallery_images 
                          : section.image_url 
                            ? [{ url: section.image_url }] 
                            : [];
                        
                        return (
                          <div key={index} className="space-y-3">
                            {sectionGallery.length > 0 && (
                              <div className="mb-4">
                                <ImageGalleryCarousel 
                                  images={sectionGallery} 
                                  altPrefix={section.heading} 
                                />
                              </div>
                            )}
                            <h4 className="text-lg font-semibold text-foreground">{section.heading}</h4>
                            <div 
                              className="text-muted-foreground [&>p]:mb-3 [&>p:last-child]:mb-0"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : town.location_access ? (
                    <div 
                      className="text-muted-foreground [&>p]:mb-3 [&>p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(town.location_access) }}
                    />
                  ) : null}

                  {/* Neighboring Towns & Areas Links */}
                  {(neighboringTowns.length > 0 || areas.length > 0) && (
                    <div className="mt-8 pt-6 border-t border-border">
                      {neighboringTowns.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-primary" />
                            Neighboring Towns
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {neighboringTowns.map((t) => (
                              <Link
                                key={t.id}
                                to={`/${t.slug}${isPreview ? '?preview=true' : ''}`}
                                className="inline-flex items-center px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm font-medium"
                              >
                                {t.name}
                                {t.state && <span className="ml-1 text-primary/70">, {t.state}</span>}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {areas.length > 0 && (
                        <div>
                          <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Building className="h-4 w-4 text-primary" />
                            Areas in {town.name}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {areas.map((a) => (
                              <Link
                                key={a.id}
                                to={`/${town.slug}/${a.slug}${isPreview ? '?preview=true' : ''}`}
                                className="inline-flex items-center px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors text-sm font-medium"
                              >
                                {a.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Market Value Report Ad */}
                  {site?.enable_market_value_report !== false && site?.seller_ad_image_url && (
                    <div className="mt-8">
                      <Link to="/market-value-report" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block">
                        <img 
                          src={site.seller_ad_image_url} 
                          alt="Free Local Market Value Report for Home Sellers" 
                          className="w-full h-auto object-cover"
                        />
                      </Link>
                    </div>
                  )}
                </div>

                {/* Amenities Column */}
                <div>
                  <h2 className="text-3xl font-display font-bold mb-8">Schools & Amenities</h2>
                  {town.amenities_sections && town.amenities_sections.length > 0 ? (
                    <div className="space-y-8">
                      {town.amenities_sections.map((section, index) => {
                        // Support both new gallery_images and legacy image_url
                        const sectionGallery: GalleryImage[] = section.gallery_images?.length 
                          ? section.gallery_images 
                          : section.image_url 
                            ? [{ url: section.image_url }] 
                            : [];
                        
                        return (
                          <div key={index} className="space-y-3">
                            {sectionGallery.length > 0 && (
                              <div className="mb-4">
                                <ImageGalleryCarousel 
                                  images={sectionGallery} 
                                  altPrefix={section.heading} 
                                />
                              </div>
                            )}
                            <h4 className="text-lg font-semibold text-foreground">{section.heading}</h4>
                            <div 
                              className="text-muted-foreground [&>p]:mb-3 [&>p:last-child]:mb-0"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : town.schools_amenities ? (
                    <div 
                      className="text-muted-foreground [&>p]:mb-3 [&>p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(town.schools_amenities) }}
                    />
                  ) : null}

                  {/* Mortgage Info Ad */}
                  {site?.enable_mortgage_info !== false && mortgageAdImageUrl && (
                    <div className="mt-8">
                      <Link to="/mortgages" className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow block">
                        <img 
                          src={mortgageAdImageUrl} 
                          alt={mortgageAdAlt} 
                          className="w-full h-auto object-cover"
                        />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Lead Form */}
        <section className="py-12 md:py-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="p-8">
              <h2 className="text-2xl font-display font-bold mb-2 text-center">
                Interested in {town.name}?
              </h2>
              <p className="text-muted-foreground text-center mb-6">
                Get in touch and we'll help you find your perfect home.
              </p>
              <LeadCaptureForm 
                siteId={town.site_id} 
                townId={town.id}
                townName={town.name}
                siteDomain={window.location.hostname}
                pagePath={`/${town.slug}`}
              />
            </Card>
          </div>
        </section>
      </main>

      <Footer 
        siteName={site?.name || 'Real Estate'} 
        towns={headerTowns} 
        areas={areas} 
        realtor={site?.realtor_assigned !== false ? realtor : null}
        enableBuyers={true}
        enableSellers={true}
        enableMortgageInfo={site?.enable_mortgage_info !== false}
      />
    </div>
    </PublicThemeProvider>
  );
}